import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

function CodeBlock({ code, language = 'html' }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="relative">
      <button
        onClick={handleCopy}
        className="absolute right-3 top-3 text-xs text-gray-500 hover:text-gray-200 transition-colors bg-gray-800 border border-gray-700 rounded px-2 py-1"
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>
      <div className="code-block pr-20">
        <pre className="text-xs leading-relaxed whitespace-pre-wrap">{code}</pre>
      </div>
    </div>
  );
}

function Section({ title, description, children }) {
  return (
    <section className="card mb-6">
      <h2 className="text-base font-semibold text-white mb-1">{title}</h2>
      {description && <p className="text-sm text-gray-400 mb-4 leading-relaxed">{description}</p>}
      {children}
    </section>
  );
}

export default function Integration() {
  const { merchant } = useAuth();
  const relayAddr = merchant?.relay_contract_addr || 'tex1p3r4hssn44pdacfmyw9cl8g7398luwvc0pp7rzjzmhz7e5gze6cqqcun9h4';

  const buttonSnippet = `<!-- Add to your checkout page -->
<script src="https://merchant.mintbridge.cloud/sdk.js"></script>

<button
  data-mintbridge-merchant="${relayAddr}"
  data-amount="1000"
  data-currency="USD"
  data-product-name="My Product"
  class="mintbridge-checkout-btn"
>
  Pay with Card
</button>`;

  const webhookSnippet = `// Express.js webhook handler example
app.post('/webhooks/mintbridge', express.raw({ type: 'application/json' }), (req, res) => {
  const sig = req.headers['x-mintbridge-signature'];

  // Verify the signature
  const event = mintbridge.webhooks.constructEvent(req.body, sig, WEBHOOK_SECRET);

  switch (event.type) {
    case 'payment.completed':
      const payment = event.data;
      // Fulfill the order
      await fulfillOrder(payment.stripe_session_id, payment.amount_sats);
      break;

    case 'chargeback.initiated':
      const dispute = event.data;
      // Mark order as disputed
      await markDisputed(dispute.stripe_dispute_id);
      break;

    case 'escrow.released':
      // Escrow has been released to your wallet
      console.log('Escrow released:', event.data.amount_sats, 'sats');
      break;
  }

  res.json({ received: true });
});`;

  const customCheckoutSnippet = `// Custom checkout integration
const mintbridge = new MintBridge({
  merchantAddress: '${relayAddr}',
  network: 'testnet', // or 'mainnet'
  onSuccess: (payment) => {
    console.log('Payment complete:', payment.tx_id);
    // Redirect to success page
    window.location.href = '/order-confirmed';
  },
  onError: (error) => {
    console.error('Payment failed:', error.message);
  },
});

// Open checkout programmatically
document.getElementById('pay-btn').addEventListener('click', () => {
  mintbridge.checkout({
    amount: 2999, // cents
    currency: 'USD',
    productName: 'Premium Plan',
    customerId: 'user_123',
  });
});`;

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-2xl font-bold text-white">Integration</h1>
          <span className="text-xs bg-yellow-500/15 border border-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full font-medium">
            Testnet
          </span>
        </div>
        <p className="text-sm text-gray-400">Add MintBridge payments to your website or app</p>
      </div>

      {/* Quick Start */}
      <Section
        title="Quick Start — Checkout Button"
        description="The easiest way to accept payments. Drop this snippet on your checkout page and you're done."
      >
        <CodeBlock code={buttonSnippet} language="html" />
        <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
          {[
            { attr: 'data-amount', desc: 'Amount in cents (e.g. 1000 = $10.00)' },
            { attr: 'data-currency', desc: 'Currency code (USD only currently)' },
            { attr: 'data-product-name', desc: 'Shown on the checkout page' },
          ].map(item => (
            <div key={item.attr} className="bg-gray-800 rounded-lg p-3">
              <code className="font-mono text-blue-400 block mb-1">{item.attr}</code>
              <span className="text-gray-500">{item.desc}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Custom JS SDK */}
      <Section
        title="Custom Integration — JavaScript SDK"
        description="Full control over the checkout flow with the MintBridge JS SDK."
      >
        <CodeBlock code={customCheckoutSnippet} language="javascript" />
      </Section>

      {/* Webhooks */}
      <Section
        title="Webhooks"
        description="Receive real-time notifications for payments, chargebacks, and escrow events. Configure your endpoint in Settings."
      >
        <div className="mb-4 space-y-2">
          {[
            { event: 'payment.completed', desc: 'A payment has settled to your wallet (95%)' },
            { event: 'payment.failed', desc: 'A payment attempt failed' },
            { event: 'chargeback.initiated', desc: 'A Stripe dispute was filed against a payment' },
            { event: 'chargeback.completed', desc: 'Escrow clawback transaction confirmed' },
            { event: 'escrow.released', desc: '5% escrow released to your wallet after confirmation window' },
          ].map(item => (
            <div key={item.event} className="flex items-start gap-3 py-2 border-b border-gray-800 last:border-0">
              <code className="font-mono text-xs text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded flex-shrink-0 mt-0.5">
                {item.event}
              </code>
              <span className="text-sm text-gray-400">{item.desc}</span>
            </div>
          ))}
        </div>
        <CodeBlock code={webhookSnippet} language="javascript" />
      </Section>

      {/* Your merchant details */}
      <Section title="Your Merchant Details">
        <div className="space-y-3">
          <div className="bg-gray-800 rounded-lg p-3">
            <div className="text-xs text-gray-400 mb-1">Relay Contract Address (use as merchant ID)</div>
            <code className="font-mono text-xs text-gray-200 break-all">{relayAddr}</code>
          </div>
          <div className="bg-gray-800 rounded-lg p-3">
            <div className="text-xs text-gray-400 mb-1">Webhook Endpoint (configure yours here)</div>
            <code className="font-mono text-xs text-gray-500">https://your-domain.com/webhooks/mintbridge</code>
          </div>
        </div>
      </Section>

      {/* API Docs placeholder */}
      <section className="card bg-gradient-to-br from-blue-900/20 to-gray-900 border-blue-800/30">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600/20 border border-blue-500/20 flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-white">Full API Documentation</h2>
            <p className="text-xs text-gray-400">REST API reference, SDKs, and sample code</p>
          </div>
        </div>
        <p className="text-sm text-gray-400 mb-4">
          Complete API documentation is coming soon. In the meantime, use the webhook patterns above and the JS SDK for integration.
        </p>
        <div className="flex items-center gap-2 text-xs text-blue-400">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          API docs launching alongside mainnet release
        </div>
      </section>
    </div>
  );
}
